import { useState, useCallback, useEffect, useMemo } from "react";
import { DateTime } from "luxon"
import { Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAxios, APIError } from "../../core/_axios";
import { managementGetAllCrops } from "../../core/_requests";
import { TemplateCrop } from "../../core/_models";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import usePageTitle from "../../hooks/usePageTitle";

type SortField = 'name' | 'last_crop' | 'created';
type SortDirection = 'asc' | 'desc';

export function CropsList() {
    usePageTitle('Manage Crops');
    const { setPageHeader } = usePageHeader();
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [crops, setCrops] = useState<TemplateCrop[]>([]);
    const [cropsCount, setCropsCount] = useState<null | number>(null);
    
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const loadCrops = useCallback(async () => {
        setLoading(true);
        setLoadingError(null);
        try {
            const {
                crops: updatedCrops,
                crops_count: updatedCropsCount
            } = await managementGetAllCrops(axios);
            setCrops(updatedCrops);
            setCropsCount(updatedCropsCount);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios]);

    // Set up page header
    useEffect(() => {
        setPageHeader('Manage Crops');
        return () => setPageHeader(null);
    }, [setPageHeader]);

    useEffect(() => {
        loadCrops();
    }, [loadCrops]);

    const sortedCrops = useMemo(() => {
        return [...crops].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = a.specific_resolution.toLowerCase();
                    bValue = b.specific_resolution.toLowerCase();
                    break;
                case 'last_crop':
                    aValue = new Date(a.latest_signature.inserted_at).getTime();
                    bValue = new Date(b.latest_signature.inserted_at).getTime();
                    break;
                case 'created':
                    aValue = new Date(a.inserted_at).getTime();
                    bValue = new Date(b.inserted_at).getTime();
                    break;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [crops, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadCrops}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return <div className="container-fixed">
        <div className="card card-grid min-w-full">
            <div className="card-body">
                <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                    <div className="scrollable-x-auto">
                        <table className="table table-fixed table-border" data-datatable-table="true">
                            {crops.length > 0 && <thead>
                                <th>
                                    <span 
                                        className={`sort ${sortField === 'name' ? sortDirection : ''} cursor-pointer`}
                                        onClick={() => handleSort('name')}
                                    >
                                        <span className="sort-label">
                                            Name
                                        </span>
                                        <span className="sort-icon">
                                        </span>
                                    </span>
                                </th>
                                <th className="w-[200px]">
                                    <span 
                                        className={`sort ${sortField === 'last_crop' ? sortDirection : ''} cursor-pointer`}
                                        onClick={() => handleSort('last_crop')}
                                    >
                                        <span className="sort-label">
                                            Last Crop
                                        </span>
                                        <span className="sort-icon">
                                        </span>
                                    </span>
                                </th>
                                <th className="w-[200px]">
                                    <span 
                                        className={`sort ${sortField === 'created' ? sortDirection : ''} cursor-pointer`}
                                        onClick={() => handleSort('created')}
                                    >
                                        <span className="sort-label">
                                            Created
                                        </span>
                                        <span className="sort-icon">
                                        </span>
                                    </span>
                                </th>
                            </thead>}
                            {crops.length > 0 ? <tbody>
                                {sortedCrops.map((crop) => {
                                    return <>
                                        <tr key={crop.unique_identifier}>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex flex-col justify-center gap-2">
                                                        <Link
                                                            to={`/admin/manage-crops/${crop.unique_identifier}`}
                                                            className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer"
                                                        >
                                                            {crop.specific_resolution}
                                                        </Link>
                                                        <div className="flex items-center justify-start">
                                                            {crop.public_data.crop_values ? <>
                                                                <span className="menu-icon items-start w-[20px] text-green-500">
                                                                    <i className="ki-filled ki-check"></i>
                                                                </span>
                                                                <div className="text-2sm text-gray-600">
                                                                    {`Mapped - ${crop.signature_count || 0} signatures`}
                                                                </div>
                                                            </> : <>
                                                                <span className="menu-icon items-start w-[20px] text-yellow-500">
                                                                    <i className="ki-filled ki-information"></i>
                                                                </span>
                                                                <div className="text-2sm text-gray-600">
                                                                    {`Not yet mapped - ${crop.signature_count || 0} signatures`}
                                                                </div>
                                                            </>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {DateTime.fromISO(crop.latest_signature.inserted_at).toRelative()}
                                            </td>
                                            <td>
                                                {DateTime.fromISO(crop.inserted_at).toRelative()}
                                            </td>
                                        </tr>
                                    </>
                                })}
                            </tbody> : <tbody>
                                <tr>
                                    <td colSpan={1} className="text-center">
                                        <div>
                                            No crops have been created.
                                        </div>
                                    </td>
                                </tr>
                            </tbody>}
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

}